import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { CheckCircle2, Home, ArrowRight } from "lucide-react";
import { PageShell, PageHero } from "@/components/site-layout";
import { verifyPayment } from "@/lib/api";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const verifyPaymentStatus = async () => {
      const params = new URLSearchParams(window.location.search);
      const reference = params.get('reference');

      if (!reference) {
        setError("No payment reference found.");
        setVerifying(false);
        return;
      }

      try {
        const paymentData = await verifyPayment(reference);
        
        // If payment was successful, update application status to payment_successful
        if (paymentData.status === 'success') {
          // The backend already updated the status to payment_successful
          // Now we can update to "registered" (enrolled) status
          // This could be done via API call if needed
        }
        
        setVerifying(false);
      } catch (err) {
        console.error('Payment verification failed:', err);
        setError("Payment verification failed. Please contact support.");
        setVerifying(false);
      }
    };

    verifyPaymentStatus();
  }, []);

  if (verifying) {
    return (
      <PageShell>
        <section className="container-x flex min-h-[70vh] items-center justify-center py-20">
          <div className="max-w-lg rounded-3xl border border-border bg-card p-10 text-center shadow-[var(--shadow-elegant)]">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="h-8 w-8 animate-spin" />
            </div>
            <h1 className="mt-6 text-3xl font-bold">Verifying payment...</h1>
            <p className="mt-3 text-muted-foreground">Please wait while we confirm your payment.</p>
          </div>
        </section>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <section className="container-x flex min-h-[70vh] items-center justify-center py-20">
          <div className="max-w-lg rounded-3xl border border-border bg-card p-10 text-center shadow-[var(--shadow-elegant)]">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-destructive/10 text-destructive">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="mt-6 text-3xl font-bold">Payment Error</h1>
            <p className="mt-3 text-muted-foreground">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="btn-primary mt-8"
            >
              <Home className="h-4 w-4" /> Return to Home
            </button>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Payment"
        title={<>Payment <span className="gradient-text">Successful</span></>}
        intro="Your payment has been processed successfully. Welcome to RevLinks Training Institute."
      />

      <section className="container-x pb-24 pt-12">
        <div className="mx-auto max-w-lg rounded-3xl border border-border bg-card p-10 text-center shadow-[var(--shadow-elegant)] animate-fade-up">
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[image:var(--gradient-primary)] text-primary-foreground">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <h1 className="mt-6 text-3xl font-bold">Payment Successful!</h1>
          <p className="mt-3 text-muted-foreground">
            Thank you for your payment. Your registration is now complete. Our admissions team will contact you with details about your training schedule.
          </p>
          <div className="mt-8 flex flex-col gap-3">
            <button
              onClick={() => navigate("/")}
              className="btn-primary"
            >
              <Home className="h-4 w-4" /> Return to Home
            </button>
            <button
              onClick={() => window.location.href = "mailto:Institute@revocubelinks.com"}
              className="btn-ghost"
            >
              <ArrowRight className="h-4 w-4" /> Contact Admissions
            </button>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
